import axios from 'axios'

export const API_BASE = (
  import.meta.env.VITE_API_URL || 'https://api.serviceflow-pro.com'
).replace(/\/+$/, '')

export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('messengerToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export type MessengerPlan = {
  id: string
  name: string
  basePrice: number
  monthlyFeeCents: number
}

export async function fetchPlan(): Promise<MessengerPlan> {
  const { data } = await api.get('/messenger/plan')
  return data.plan
}

export async function signupMessenger(body: {
  email: string
  password: string
  firstName: string
  lastName: string
  businessName: string
  phone?: string
  agreeToTerms: boolean
}) {
  const { data } = await api.post('/messenger/signup', body)
  return data as {
    success: boolean
    token: string
    businessId: string
    checkoutUrl: string
    planId: string
  }
}

export async function login(email: string, password: string) {
  const { data } = await api.post('/auth/login', { email, password })
  return data as { token?: string; accessToken?: string; user?: { businessId?: string } }
}

export async function createCheckout() {
  const { data } = await api.post('/messenger/checkout')
  return data as { checkoutUrl: string }
}

export async function startMetaOAuth(returnTo: string) {
  const { data } = await api.get('/meta-messenger/oauth/start', {
    params: { returnTo },
  })
  return data as { authorizeUrl: string }
}

export async function listPendingPages() {
  const { data } = await api.get('/meta-messenger/oauth/pending-pages')
  return data as { pages: Array<{ id: string; name: string }> }
}

export async function selectMetaPage(pageId: string) {
  const { data } = await api.post('/meta-messenger/oauth/select-page', { pageId })
  return data
}

export async function selectMetaPages(pageIds: string[]) {
  const { data } = await api.post('/meta-messenger/oauth/select-pages', { pageIds })
  return data as {
    connected: Array<{ metaPageId: string; pageName: string; webhookSlug: string }>
  }
}

export type ConnectedFacebookPage = {
  id: string
  metaPageId: string
  pageName: string | null
  webhookSlug: string | null
  webhookUrl: string | null
  isActive: boolean
  createdAt: string
}

export async function listConnectedPages() {
  const { data } = await api.get('/meta-messenger/oauth/pages')
  return data as { pages: ConnectedFacebookPage[] }
}

export async function disconnectMetaPage(metaPageId: string) {
  const { data } = await api.delete(`/meta-messenger/oauth/pages/${metaPageId}`)
  return data
}

export type MessengerConversationSummary = {
  id: string
  messengerPsid: string
  participantName: string | null
  participantProfilePic: string | null
  metaPageId: string | null
  pageName: string | null
  lastMessageText: string | null
  lastMessageDirection: 'INBOUND' | 'OUTBOUND' | null
  lastMessageAt: string | null
  updatedAt: string
}

export type MessengerInboxMessage = {
  id: string
  direction: 'INBOUND' | 'OUTBOUND'
  text: string
  metaMessageId: string | null
  createdAt: string
}

export type MessengerPageSubscription = {
  metaPageId: string
  pageName: string | null
  webhookSlug: string | null
  webhookUrl: string | null
  isActive: boolean
  subscribed: boolean
  subscribedFields: string[]
  metadata: {
    name: string | null
    about: string | null
    category: string | null
  }
}

export type MessengerWebhookEventRow = {
  id: string
  conversationId: string
  participantName: string | null
  messengerPsid: string
  direction: 'INBOUND' | 'OUTBOUND'
  text: string
  createdAt: string
  metaMessageId: string | null
}

export async function listMessengerConversations(metaPageId?: string) {
  const { data } = await api.get('/meta-messenger/conversations', {
    params: metaPageId ? { metaPageId } : undefined,
  })
  return data as { conversations: MessengerConversationSummary[] }
}

export async function listMessengerMessages(conversationId: string) {
  const { data } = await api.get(
    `/meta-messenger/conversations/${encodeURIComponent(conversationId)}/messages`
  )
  return data as { messages: MessengerInboxMessage[] }
}

export async function replyToMessengerConversation(conversationId: string, text: string) {
  const { data } = await api.post(
    `/meta-messenger/conversations/${encodeURIComponent(conversationId)}/reply`,
    { text }
  )
  return data as { message: MessengerInboxMessage }
}

export async function getMessengerPageSubscription(metaPageId: string) {
  const { data } = await api.get(
    `/meta-messenger/pages/${encodeURIComponent(metaPageId)}/subscription`
  )
  return data as { subscription: MessengerPageSubscription }
}

export async function subscribeMessengerPage(metaPageId: string) {
  const { data } = await api.post(
    `/meta-messenger/pages/${encodeURIComponent(metaPageId)}/subscribe`
  )
  return data as { subscription: MessengerPageSubscription }
}

export async function unsubscribeMessengerPage(metaPageId: string) {
  const { data } = await api.delete(
    `/meta-messenger/pages/${encodeURIComponent(metaPageId)}/subscribe`
  )
  return data as { subscription: MessengerPageSubscription }
}

export async function listMessengerRecentEvents(metaPageId: string, limit = 15) {
  const { data } = await api.get(
    `/meta-messenger/pages/${encodeURIComponent(metaPageId)}/recent-events`,
    { params: { limit } }
  )
  return data as { events: MessengerWebhookEventRow[] }
}

export async function getMetaConfig() {
  const { data } = await api.get('/businesses/messenger-kb/meta-config')
  return data as {
    pages?: ConnectedFacebookPage[]
    config: {
      metaPageId?: string
      pageName?: string | null
      webhookSlug?: string | null
      isActive?: boolean
      hasPageAccessToken?: boolean
    } | null
  }
}

export async function listKbDocuments() {
  const { data } = await api.get('/businesses/messenger-kb/documents')
  return data as {
    documents: Array<{
      id: string
      title: string | null
      sourceType: string
      createdAt: string
      chunkCount: number
    }>
  }
}

export async function getKbDocument(id: string) {
  const { data } = await api.get(`/businesses/messenger-kb/documents/${id}`)
  return data as {
    document: {
      id: string
      title: string | null
      sourceType: string
      sourceId: string | null
      rawContent: string
      createdAt: string
      chunks: Array<{ id: string; chunkIndex: number; text: string }>
    }
  }
}

export async function uploadKbCsv(file: File) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post('/businesses/messenger-kb/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteKbDocument(id: string) {
  const { data } = await api.delete(`/businesses/messenger-kb/documents/${id}`)
  return data
}

export type MessengerKbApiKeyPublic = {
  businessId: string
  enabled: boolean
  keyId: string | null
  lastFour: string | null
  createdAt: string | null
  rotatedAt: string | null
  lastUsedAt: string | null
  apiKey?: string
}

export type MessengerKbUsage = {
  storageBytes: number
  storageLimitBytes: number
  storagePackQty: number
  requestsToday: number
  requestLimitPerDay: number
  requestPackQty: number
  dayUtc: string
  nearStorageLimit: boolean
  nearRequestLimit: boolean
  tokensUsedThisMonth: number
  tokenLimitPerMonth: number
  tokenPackQty: number
  monthUtc: string
  nearTokenLimit: boolean
  chatModel: string
  allowedChatModels: string[]
  usesPlatformOpenAiKey: boolean
}

export type MessengerKbPacks = {
  storagePackQty: number
  requestPackQty: number
  tokenPackQty: number
  storagePackMonthlyCents: number
  requestPackMonthlyCents: number
  tokenPackMonthlyCents: number
  storagePackBytes: number
  requestPackPerDay: number
  tokenPackTokens: number
}

export async function getKbApiKey() {
  const { data } = await api.get('/messenger/kb-api-keys')
  return data as { success: boolean; key: MessengerKbApiKeyPublic }
}

export async function createOrRotateKbApiKey() {
  const { data } = await api.post('/messenger/kb-api-keys')
  return data as { success: boolean; key: MessengerKbApiKeyPublic }
}

export async function revokeKbApiKey() {
  const { data } = await api.delete('/messenger/kb-api-keys')
  return data as { success: boolean }
}

export async function getKbUsage() {
  const { data } = await api.get('/messenger/kb-usage')
  return data as {
    success: boolean
    usage: MessengerKbUsage
    packs: MessengerKbPacks
  }
}

export async function buyStoragePack() {
  const { data } = await api.post('/messenger/kb-usage/storage-packs')
  return data as { success: boolean; usage: MessengerKbUsage; packs: MessengerKbPacks }
}

export async function buyRequestPack() {
  const { data } = await api.post('/messenger/kb-usage/request-packs')
  return data as { success: boolean; usage: MessengerKbUsage; packs: MessengerKbPacks }
}

export async function buyTokenPack() {
  const { data } = await api.post('/messenger/kb-usage/token-packs')
  return data as { success: boolean; usage: MessengerKbUsage; packs: MessengerKbPacks }
}

export async function updateChatModel(model: string) {
  const { data } = await api.put('/messenger/chat-model', { model })
  return data as { success: boolean; chatModel: string; usage: MessengerKbUsage }
}
